import Dropdown from "@/components/Dropdown";
import Checkbox from "@/components/Checkbox";
import Search from "@/components/Search";
import { CheckboxVariant, SearchVariant, Size } from "@ever-tutor/et-ui";
import {
  accountLabel,
  selectableTeacherUserIds,
  type AccessibleAccount,
} from "@/hooks/useAccounts";
import { useEffect, useMemo, useState } from "react";
import FilterActions from "./FilterActions";

interface TeacherFilterProps {
  accounts: AccessibleAccount[];
  // The user_ids whose analytics are shown. Empty means "view my own students".
  selectedUserIds: number[];
  onChange: (userIds: number[]) => void;
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Dashboard-only teacher selector. Lets an org admin (all org teachers) or a
 * multi-account co-teacher pick whose analytics to view. Drives the
 * `/analytics/sessions` user_ids param; multiple teachers union their groups.
 */
const TeacherFilter = ({
  accounts,
  selectedUserIds,
  onChange,
  isOpen,
  onToggle,
}: TeacherFilterProps) => {
  const own = accounts.find((a) => a.is_you);
  const ownUserId = own?.user_id ?? null;

  const selectableAccounts = useMemo(
    () => accounts.filter((a) => a.has_analytics),
    [accounts],
  );

  // Only ids the dropdown can actually show; keeps `pending` from carrying a
  // stale, invisible id (e.g. a teacher who lost analytics) through Apply.
  const selectableIds = useMemo(
    () => selectableTeacherUserIds(accounts),
    [accounts],
  );

  const [pending, setPending] = useState<number[]>(() =>
    selectedUserIds.filter((id) => selectableIds.has(id)),
  );
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setPending(selectedUserIds.filter((id) => selectableIds.has(id)));
      setSearchQuery("");
    }
  }, [isOpen, selectedUserIds, selectableIds]);

  const title = useMemo(() => {
    if (selectedUserIds.length === 0) return "Teachers";
    if (selectedUserIds.length === 1) {
      const sel = accounts.find((a) => a.user_id === selectedUserIds[0]);
      if (sel) return sel.is_you ? "Teachers" : accountLabel(sel);
    }
    return `${selectedUserIds.length} Teachers`;
  }, [selectedUserIds, accounts]);

  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return selectableAccounts;
    const query = searchQuery.toLowerCase();
    return selectableAccounts.filter((account) =>
      accountLabel(account).toLowerCase().includes(query),
    );
  }, [selectableAccounts, searchQuery]);

  // Membership checks run inside loops over every org teacher.
  const pendingSet = useMemo(() => new Set(pending), [pending]);

  const allSelected =
    filteredAccounts.length > 0 &&
    filteredAccounts.every((a) => pendingSet.has(a.user_id));

  const toggle = (userId: number) => {
    setPending((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleSelectAll = () => {
    if (allSelected) {
      const visible = new Set(filteredAccounts.map((a) => a.user_id));
      setPending((prev) => prev.filter((id) => !visible.has(id)));
    } else {
      setPending((prev) => {
        const next = [...prev];
        const added = new Set(prev);
        filteredAccounts.forEach((a) => {
          if (!added.has(a.user_id)) {
            added.add(a.user_id);
            next.push(a.user_id);
          }
        });
        return next;
      });
    }
  };

  const handleApply = () => {
    onChange(pending);
    onToggle();
  };

  const handleRemove = () => {
    setPending([]);
    onChange([]);
    onToggle();
  };

  return (
    <Dropdown
      title={title}
      isOpen={isOpen}
      onToggle={onToggle}
      variant="analytics"
      size={Size.SM}
      showActive={selectedUserIds.length > 0}
    >
      <div className="flex flex-col gap-4">
        <Search
          placeholder="Search for a Teacher"
          value={searchQuery}
          onChange={setSearchQuery}
          showClearButton={false}
          inputClassName="text-xs"
          height="40px"
          variant={SearchVariant.OUTLINE}
          size={Size.SM}
        />
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2">
            <Checkbox
              checked={allSelected}
              onChange={handleSelectAll}
              variant={CheckboxVariant.SIMPLE}
            >
              Select All
            </Checkbox>
          </label>
          <div className="flex flex-col gap-1 max-h-60 overflow-auto">
            {filteredAccounts.length === 0 && (
              <p role="status" className="p-2 text-sm text-gray-500">
                No matching teachers
              </p>
            )}
            {filteredAccounts.map((account) => (
              <div
                key={account.teacher_id}
                data-selected={pendingSet.has(account.user_id)}
                className={`et-filter-option hover:bg-gray-50 p-2 ${
                  pendingSet.has(account.user_id) ? "bg-[#EDFFF8]" : ""
                }`}
              >
                <Checkbox
                  aria-label={`${accountLabel(account)}${account.user_id === ownUserId ? " (you)" : ""}`}
                  checked={pendingSet.has(account.user_id)}
                  onChange={() => toggle(account.user_id)}
                  labelClassName="text-sm font-mulish text-[#252525]"
                  variant={CheckboxVariant.SIMPLE}
                >
                  {accountLabel(account)}
                  {account.user_id === ownUserId && (
                    <span className="ml-1 text-xs text-gray-400">(you)</span>
                  )}
                </Checkbox>
              </div>
            ))}
          </div>
        </div>
        <FilterActions onApply={handleApply} onClear={handleRemove} />
      </div>
    </Dropdown>
  );
};

export default TeacherFilter;
